// app/src/features/timeline/renderers/renderEvt.jsx
// Timeline render helpers moved verbatim out of AppInner (app/src/App.jsx) by PKG-04 module #1. Each
// takes the column's render context first; the original parameters follow.
import React from "react";
import { C } from "../../../lib/colors.js";
import { forkNodeLabel } from "../../../lib/forecast/branchPremise.js";
import { eventKey } from "../../../lib/forecast/eventIdentity.js";
import { ui } from "../../../lib/telemetry/instrument.js";
import { LABEL_H, LABEL_MIN_PPD } from "../../../lib/timeline/geometry.js";
import { haloBox } from "../../../lib/timeline/hitHalo.js";
import { LABEL_FADE_MS } from "../../../lib/timeline/labelLayout.js";
import { clampLabelShift } from "../../../lib/timeline/laneBand.js";
import {
  computeEventGeom,
  renderEventPie,
  renderLabelTether,
  resolveEventStatus,
  rowKey,
} from "./eventHelpers.jsx";
import { renderEventBell } from "./renderEventBell.jsx";
import {
  LABEL_GAP_SCALE,
  NEUTRAL_LABEL_GAP_SCALE,
  SPINE_DASH_PX,
  STREAM_HALF,
  TETHER_LINE_H,
} from "./constants.js";

/** The fork node's hover copy: its premise status, as the node draws it, for the tooltip. */
function forkHoverCopy(ev, triggerCtx) {
  const outcome = triggerCtx.premiseUndetermined
    ? "undetermined"
    : triggerCtx.overdue
      ? "overdue"
      : triggerCtx.watching
        ? "watching"
        : null;
  return {
    ...ev,
    _isTrigger: true,
    _scenarioOutcome: outcome,
    _recheckable: !!triggerCtx.recheckable,
  };
}

export function renderEvt(
  ctx,
  ev,
  screenX,
  spineY,
  color,
  above,
  animIndex,
  animState,
  _stagger = 0,
  rowId = "main",
  _compact = false,
  triggerCtx = null,
  branchForkMs = null,
  ghost = false,
  haloR = null,
) {
  const {
    active,
    cSize,
    forecastTracking,
    hov,
    hovEvtRef,
    hoveredBranchTrigger,
    laneLeftEdge,
    longPressRef,
    ppd,
    setHov,
    setHovPos,
    setHoveredBranch,
    setHoveredBranchTrigger,
    setSelEvt,
  } = ctx;
  const { hovKey, r, d, ih, col, labelCol, circleY } = computeEventGeom(
    ctx,
    ev,
    rowId,
    color,
    above,
    spineY,
    // A branch fork node must BE the corner — see computeEventGeom's circleY.
    !!triggerCtx,
  );
  const isTrigger = !!triggerCtx;
  // NO Y correction: ForecastLane lifts every horizontal dashed segment by half its thickness
  // (SPINE_DASH_HALF), so a circle centered on spineY already sits on the line's visual center.
  //
  // X is different and still needs the nudge: the branch FORK header sits on the dashed VERTICAL
  // connector, whose `borderLeft` renders RIGHTWARD from its left edge and was NOT lifted, so its
  // visual centre really is half a thickness right of the logical x.
  const forkXNudge = isTrigger && ev._neutral && !ev.isPast ? SPINE_DASH_PX / 2 : 0;
  const { isResolvedOccurred, isResolvedNotOccurred } = resolveEventStatus(
    ev,
    active,
    forecastTracking,
  );
  const titleText = (() => {
    // Triggers are user-authored branch descriptions, so they get a longer word cap before
    // ellipsizing.
    const cap = isTrigger ? 12 : 6;
    const w = (ev.title || "").split(" ");
    const t = w.length <= cap ? ev.title || "" : w.slice(0, cap).join(" ") + "...";
    return isTrigger ? forkNodeLabel(t, triggerCtx.sense) : t;
  })();
  // Nodes under the opaque left panel are not drawn at all.
  if (screenX < Math.max(-60, laneLeftEdge - 24) || screenX > cSize.w + 60) return null;
  const isAnim = animState !== null;
  const revealed = !isAnim || (animIndex !== undefined && animIndex < animState.revealCount);
  if (!revealed) return null;
  const isFinal =
    isAnim &&
    animIndex !== undefined &&
    animIndex === animState.revealCount - 1 &&
    animIndex === animState.totalEvents - 2;

  // A VALENCED trigger's label clears the YES/NO stream channel, which is what STREAM_HALF
  // measures. A NEUTRAL one sits ON the spine with no channel to clear, so its label is placed
  // off the CIRCLE's edge. Scale the gap rather than the offset: the radius tracks the trigger's
  // impact, so tightening the raw offset would move a large circle's label far more than a small
  // one's, and could even put it inside the circle.
  const neutralLabelGap = (offset) => r + (offset - r) * NEUTRAL_LABEL_GAP_SCALE * LABEL_GAP_SCALE;
  const labelY = ev._neutral
    ? above
      ? spineY - neutralLabelGap(STREAM_HALF) - LABEL_H
      : spineY + neutralLabelGap(STREAM_HALF + 4)
    : above
      ? Math.min(circleY - LABEL_H - 2, spineY - STREAM_HALF * LABEL_GAP_SCALE - LABEL_H)
      : spineY + (STREAM_HALF + 4) * LABEL_GAP_SCALE;
  // Keep the label box inside the visible lane; none for a node under the panel.
  const labelFit = clampLabelShift(screenX + _stagger, 104, {
    minX: laneLeftEdge,
    maxX: cSize.w || 800,
  });

  // A label that just closed, fading out over its dot: text and tether only — no circle,
  // no targets, no test ids, no fade-in of its own — so it can neither take a click nor
  // flash. Hidden outright for reduced motion (GlobalStyles `.tl-label-ghost`).
  if (ghost)
    return labelFit.hidden ? null : (
      <div
        key={"ghost-" + rowKey(rowId, eventKey(ev))}
        className="tl-label-ghost"
        style={{
          position: "absolute",
          left: screenX,
          top: 0,
          zIndex: 9,
          pointerEvents: "none",
          animation: `simpleFadeOut ${LABEL_FADE_MS}ms ease-out forwards`,
        }}
      >
        {renderLabelTether(ev, above, labelY, circleY, r, LABEL_H / TETHER_LINE_H)}
        <div
          style={{
            position: "absolute",
            left: -52 + labelFit.shift,
            top: labelY,
            width: 104,
            height: LABEL_H,
            display: "flex",
            flexDirection: "column",
            justifyContent: above ? "flex-end" : "flex-start",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <span
            style={{
              fontSize: 12,
              lineHeight: "15px",
              color: labelCol,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {ev.title}
          </span>
        </div>
      </div>
    );

  const evtHoverEnter = (e) => {
    // The premise status rides on the hovered COPY, never on the stored event — it is a
    // view fact about this branch's premise, not something the fork event owns, and
    // writing it through would persist into the fork jsonb.
    hovEvtRef.current = isTrigger ? forkHoverCopy(ev, triggerCtx) : ev;
    setHov(hovKey);
    setHovPos({ x: e.clientX, y: e.clientY });
    if (isTrigger) setHoveredBranchTrigger(triggerCtx.branchId);
  };
  const evtHoverMove = (e) => setHovPos({ x: e.clientX, y: e.clientY });
  // A branch FORK node (isTrigger) deliberately opens nothing — that label takes no click
  // either.
  const evtOpen = (e) => {
    if (isTrigger) {
      e.stopPropagation();
      return;
    }
    // Long-press on the CIRCLE collapses the event to a dot; swallow the click it
    // fires on release so it doesn't also open the modal. Only the circle arms this
    // (its onMouseDown), so from the label the guard is simply never true.
    if (longPressRef.current.fired && longPressRef.current.evId === eventKey(ev)) {
      e.stopPropagation();
      longPressRef.current.fired = false;
      return;
    }
    setSelEvt({ ...ev, _rowId: rowId });
  };
  const evtHoverLeave = () => {
    hovEvtRef.current = null;
    setHov(null);
    if (longPressRef.current.evId === eventKey(ev)) clearTimeout(longPressRef.current.timer);
    if (isTrigger) setHoveredBranchTrigger((prev) => (prev === triggerCtx.branchId ? null : prev));
  };
  // `hov` also carries non-event keys (`res-...`, `rbr...`); only an event's key is
  // `rowId::evId`, so the separator is the test.
  const anyEvtHovered = typeof hov === "string" && hov.includes("::");
  const recede = anyEvtHovered && !ih;
  const halo = haloBox(r, circleY, haloR);

  return (
    <React.Fragment key={eventKey(ev)}>
      {renderEventBell(ctx, ev, screenX, rowId, above, spineY, circleY, r, ih, branchForkMs)}
      {/* Hover raises this layer above neighboring events and the bell-curve underlay. */}
      <div
        style={{
          position: "absolute",
          left: screenX,
          top: 0,
          zIndex: ih ? 20 : 10,
          // Emphasis by CONTRAST, not by size: the hovered event stays at full brightness
          // and every other one recedes. This layer holds the circle, its connector and its
          // label, so one opacity dims the whole event as a unit.
          opacity: recede ? 0.65 : 1,
          transition: "opacity .12s",
        }}
      >
        <div
          data-tour-spotlight={!ev.isPast ? "trigger" : undefined}
          data-tour-trigger-dir={!ev.isPast ? (ev.direction === "no" ? "no" : "yes") : undefined}
          onMouseEnter={evtHoverEnter}
          onMouseMove={evtHoverMove}
          onMouseLeave={evtHoverLeave}
          style={{
            position: "absolute",
            left: halo.left + forkXNudge,
            top: halo.top,
            width: halo.size,
            height: halo.size,
            borderRadius: "50%",
            pointerEvents: "auto",
            zIndex: 9,
          }}
        >
          {/* data-testid is the RUN's id, which is what the e2e specs address; ids are not
               rewritten, so it is unique only within a vintage. `data-occurrence-key`
               disambiguates any test that drives an accumulated multi-vintage timeline. */}
          <div
            data-testid={`evt-pie-${ev.id}`}
            data-clears-question=""
            data-occurrence-key={eventKey(ev)}
            {...ui("timeline.event", ev.id)}
            onMouseDown={(e) => {
              e.stopPropagation();
              if (isTrigger) return;
              longPressRef.current = {
                timer: setTimeout(() => {
                  longPressRef.current.fired = true;
                  collapseEvt(ctx, rowId, eventKey(ev));
                }, 500),
                fired: false,
                evId: eventKey(ev),
              };
            }}
            onMouseUp={() => {
              if (longPressRef.current.evId === eventKey(ev))
                clearTimeout(longPressRef.current.timer);
            }}
            onClick={evtOpen}
            style={{
              position: "absolute",
              left: halo.inset,
              top: halo.inset,
              cursor: isTrigger ? "default" : "pointer",
              transition: "transform .12s",
              // No hover scale: growing the circle moves it under the cursor and shifts its
              // neighbours' apparent spacing, so brightness carries the emphasis instead (see the
              // layer opacity above). `isFinal` keeps its own scale, which is not a hover state.
              transform: isFinal ? "scale(1.4)" : "scale(1)",
              transformOrigin: "center",
              animation: isAnim ? "popIn 0.35s ease-out forwards" : "none",
            }}
          >
            {renderEventPie(
              ctx,
              ev,
              r,
              d,
              ih,
              col,
              isResolvedOccurred,
              isResolvedNotOccurred,
              triggerCtx,
            )}
          </div>
        </div>
        {/* The buttons live INSIDE the hover region, so moving the cursor onto a button
             (which extends past the region's bounding rect) does not fire mouseLeave and
             flicker them. */}
        {isTrigger &&
          (() => {
            const showBtns = hoveredBranchTrigger === triggerCtx.branchId;
            const btnW = 104,
              btnH = 17;
            const hoverR = r * 4;
            const cx = r,
              cy = circleY + r;
            // Button positions are relative to the halo's top-left (which is at cx-hoverR, cy-hoverR).
            const btnLeftRel = hoverR + 8;
            const btnTopBaseRel = hoverR - btnH - 3;
            const btnTopCollapseRel = hoverR + 3;
            return (
              <div
                onMouseEnter={(e) => {
                  setHoveredBranchTrigger(triggerCtx.branchId);
                  setHoveredBranch(triggerCtx.branchId);
                  hovEvtRef.current = forkHoverCopy(ev, triggerCtx);
                  setHov(hovKey);
                  setHovPos({ x: e.clientX, y: e.clientY });
                }}
                onMouseMove={(e) => setHovPos({ x: e.clientX, y: e.clientY })}
                onMouseLeave={() => {
                  setHoveredBranchTrigger((prev) => (prev === triggerCtx.branchId ? null : prev));
                  setHoveredBranch((prev) => (prev === triggerCtx.branchId ? null : prev));
                  hovEvtRef.current = null;
                  setHov(null);
                }}
                style={{
                  position: "absolute",
                  left: cx - hoverR,
                  top: cy - hoverR,
                  width: hoverR * 2,
                  height: hoverR * 2,
                  borderRadius: "50%",
                  pointerEvents: "auto",
                  zIndex: 25,
                }}
              >
                <button
                  {...ui("timeline.branch-clear", triggerCtx.branchId)}
                  onClick={(e) => {
                    e.stopPropagation();
                    triggerCtx.onClearBranch();
                  }}
                  style={{
                    position: "absolute",
                    left: btnLeftRel,
                    top: btnTopBaseRel,
                    width: btnW,
                    height: btnH,
                    boxSizing: "border-box",
                    background: C.toolbarBtn,
                    color: C.gray,
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    letterSpacing: 0,
                    cursor: "pointer",
                    pointerEvents: showBtns ? "auto" : "none",
                    transform: showBtns ? "scale(1)" : "scale(0)",
                    transformOrigin: "left center",
                    transition: "transform 0.2s cubic-bezier(0.34,1.56,0.64,1), opacity 0.15s",
                    opacity: showBtns ? 1 : 0,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxShadow: `0 2px 8px ${C.shadow}`,
                    padding: "0 8px",
                    fontFamily: "inherit",
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  Clear scenario
                </button>
                <button
                  {...ui("timeline.branch-collapse", triggerCtx.branchId)}
                  onClick={(e) => {
                    e.stopPropagation();
                    triggerCtx.onToggleCollapse();
                  }}
                  style={{
                    position: "absolute",
                    left: btnLeftRel,
                    top: btnTopCollapseRel,
                    width: btnW,
                    height: btnH,
                    boxSizing: "border-box",
                    background: C.toolbarBtn,
                    color: C.gray,
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    letterSpacing: 0,
                    cursor: "pointer",
                    pointerEvents: showBtns ? "auto" : "none",
                    transform: showBtns ? "scale(1)" : "scale(0)",
                    transformOrigin: "left center",
                    transition:
                      "transform 0.2s cubic-bezier(0.34,1.56,0.64,1) 0.04s, opacity 0.15s",
                    opacity: showBtns ? 1 : 0,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxShadow: `0 2px 8px ${C.shadow}`,
                    padding: "0 8px",
                    fontFamily: "inherit",
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {triggerCtx.isCollapsed ? "Expand" : "Collapse"}
                </button>
              </div>
            );
          })()}
        {ppd >= LABEL_MIN_PPD &&
          (isTrigger ? (
            // Narrows (then hides) rather than running under the left panel.
            screenX - r - 16 - laneLeftEdge < 48 ? null : (
              <div
                style={{
                  position: "absolute",
                  left: -r - 10 - Math.min(160, screenX - r - 16 - laneLeftEdge),
                  top: circleY,
                  width: Math.min(160, screenX - r - 16 - laneLeftEdge),
                  height: d,
                  textAlign: "right",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  // The BOX stays click-through — it is 160px of mostly empty space reserved
                  // for right-aligned text, and making all of it hoverable would put a wide
                  // dead-air target over the timeline. Only the text itself opts back in.
                  pointerEvents: "none",
                }}
              >
                <div
                  onMouseEnter={evtHoverEnter}
                  onMouseMove={evtHoverMove}
                  onMouseLeave={evtHoverLeave}
                  title={titleText}
                  style={{
                    fontSize: 12,
                    color: ih ? C.white : labelCol,
                    lineHeight: "15px",
                    pointerEvents: "auto",
                    cursor: "default",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {titleText}
                </div>
              </div>
            )
          ) : labelFit.hidden ? null : (
            // The label slides to stay inside the visible lane (clear of
            // the left panel and the right edge) and truncates to two lines with an ellipsis;
            // the signposts table below carries every name in full.
            <>
              {renderLabelTether(ev, above, labelY, circleY, r, LABEL_H / TETHER_LINE_H)}
              <div
                // Measured by the onboarding TourSpotlight so the bulb clears the label
                // on whichever side it actually landed. Under the neutral (on-spine)
                // treatment that side ALTERNATES and is not the event's direction, so
                // the bulb cannot infer it — see components/Panels.jsx TourSpotlight.
                data-tour-label=""
                data-clears-question=""
                className="tl-fade"
                style={{
                  position: "absolute",
                  left: -52 + _stagger + labelFit.shift,
                  top: labelY,
                  width: 104,
                  height: LABEL_H,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: above ? "flex-end" : "flex-start",
                  alignItems: "center",
                  textAlign: "center",
                  // Runs once per mount (a dot → label swap remounts), never on a pan/zoom
                  // re-render.
                  animation: `simpleFadeIn ${LABEL_FADE_MS}ms ease-out both`,
                  // The box stays click-through; only the text opts back in.
                  pointerEvents: "none",
                }}
              >
                <span
                  {...ui("timeline.event-label", ev.id)}
                  onMouseEnter={evtHoverEnter}
                  onMouseMove={evtHoverMove}
                  onMouseLeave={evtHoverLeave}
                  onClick={evtOpen}
                  title={ev.title}
                  style={{
                    pointerEvents: "auto",
                    cursor: "pointer",
                    fontSize: 12,
                    lineHeight: "15px",
                    color: ih ? C.white : labelCol,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    maxWidth: 104,
                  }}
                >
                  {ev.title}
                </span>
              </div>
            </>
          ))}
      </div>
    </React.Fragment>
  );
}

export function collapseEvt(ctx, rowId, evId) {
  const { setCollapsedIds, setPromotedIds } = ctx;
  return setCollapsedIds((prev) => {
    const key = rowKey(rowId, evId);
    const next = new Set(prev);
    next.add(key);
    setPromotedIds((p) => {
      const n = new Set(p);
      n.delete(key);
      return n;
    });
    return next;
  });
}
